import {useEffect, useState} from "react";
import {axiosInstance} from "../../../Api/AxiosConfig";
import {formatIntegerNumberWithThousandSeparator, friendlyDate, getFormattedDate} from "../../../Hooks/UseGenericFunctions";
import {DataTable} from "./DataTable";
import {DataListModal} from "./DataListModal";
import {useDisclosure} from "@mantine/hooks";
import {ArrowRightCircleIcon} from "@heroicons/react/24/outline";

const BasicCard = ({children}) => {
    return (
        <div className="relative rounded-xl bg-white p-5 shadow-md max-w-[400px]">
            {children}
        </div>
    )
}

const BasicCardTwoColumns = ({childrenCol1, childrenCol2, colorCol2, onClick}) => {
    return (
        <div className={`relative rounded-xl flex flex-row border w-max ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
            <div className="bg-white p-5 rounded-l-xl w-[300px]">
                {childrenCol1}
            </div>
            <div className={`p-5 rounded-r-xl flex items-center justify-center w-[200px] text-3xl font-extrabold ${colorCol2}`}>
                {childrenCol2}
            </div>
        </div>
    )
}

const TitleAndDescriptionSection = ({title, description, clickable = false}) => {
    return (
        <div className="flex flex-col">
            <div className="text-lg font-semibold flex items-center justify-between">
                <div>{title}</div>
                {clickable && <div className="ml-4"><ArrowRightCircleIcon className="w-6 h-6" /></div>}
            </div>
            <div className="text-sm text-gray-600">
                {description}
            </div>
        </div>
    )
}

const StatisticsCardSingleStat = ({title, description, stat, onClick, bgColor}) => {
    return (
        <>
            <BasicCardTwoColumns
                childrenCol1={
                    <TitleAndDescriptionSection title={title} description={description} clickable={onClick}/>
                }
                childrenCol2={stat}
                colorCol2={`bg-gradient-to-br ${bgColor} text-white`}
                onClick={onClick}
            >
            </BasicCardTwoColumns>
        </>
    )
}

export const BackOfficeDashboard = () => {
    const [opened, {open, close}] = useDisclosure(false);
    const [data, setData] = useState(null);
    const [tableData, setTableData] = useState(null);

    const getStatistics = async (statIdentifier) => {
        try {
            const response = await axiosInstance.get(`/stats/${statIdentifier}`);
            const {data, description, name} = response.data;
            return {data, description, name};
        } catch (error) {
            console.error("Error fetching data:", error);
            return null;
        }
    };

    const configuration = [
        {key: "total_number_of_invoices", gradient: "from-blue-500 to-blue-700"},
        {key: "total_number_of_invoice_types", gradient: "from-purple-500 to-purple-700"},
        {key: "last_invoice_paid", gradient: "from-green-500 to-green-700"},
        {key: "count_invoice_without_type", tableData: "list_invoices_without_type", gradient: "from-yellow-500 to-yellow-700"},
        {key: "list_invoices_without_type", gradient: ""},
        {key: "unpaid_vendor_invoices", gradient: "from-red-500 to-red-700"},
        {key: "paid_vendor_invoices", gradient: "from-blue-500 to-blue-700"},
        {key: "paid_customer_invoices", gradient: "from-blue-500 to-blue-700"},
        {key: "unpaid_customer_invoices", tableData: "list_unpaid_customer_invoices", gradient: "from-red-500 to-red-700"},
        {key: "list_unpaid_customer_invoices", gradient: ""},
        {key: "number_checked_in_cars_today", tableData: "list_checked_in_cars_today", gradient: "from-green-500 to-green-700"},
        {key: "list_checked_in_cars_today", gradient: ""},
        {key: "number_checked_out_cars_today", tableData: "list_checked_out_cars_today", gradient: "from-green-500 to-green-700"},
        {key: "list_checked_out_cars_today", gradient: ""},
        {key: "number_sold_unpaid_cars", tableData: "list_sold_unpaid_cars", gradient: "from-green-500 to-green-700"},
        {key: "list_sold_unpaid_cars", gradient: ""},
    ];

    useEffect(() => {
        const fetchAllStatistics = async () => {
            const results = await Promise.all(
                configuration.map(async ({key, gradient, tableData}) => {
                    const stat = await getStatistics(key);
                    return {key, stat, gradient, tableData};
                })
            );

            const statsObject = results.reduce((acc, {key, stat, gradient, tableData}) => {
                acc[key] = {...stat, gradient, tableData};
                return acc;
            }, {});

            setData(statsObject);
        };

        fetchAllStatistics();
    }, []);

    const showTableData = (key) => {
        const configItem = configuration.find(item => item.key === key);

        if (configItem && data[key].data.length > 0) {
            setTableData(data[key].data);
            console.log(data[key].data);
            open();
        } else {
            alert("No data available for this statistic.");
        }
    }

    if (data) {
        return (
            <>
                <div className="w-screen h-screen p-8">
                    <div className="flex flex-col mb-16">
                        <h1 className="text-2xl font-bold">Backoffice Dashboard</h1>
                        <p className="text-gray-500">Direct Database Statistics</p>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <StatisticsCardSingleStat
                            title={data.total_number_of_invoices?.name} bgColor={configuration.find(item => item.key === "total_number_of_invoices").gradient}
                            description={data.total_number_of_invoices?.description}
                            stat={formatIntegerNumberWithThousandSeparator(data.total_number_of_invoices.data[0]?.Number)}
                        />
                        <StatisticsCardSingleStat
                            title={data.total_number_of_invoice_types?.data[0]?.name} bgColor={configuration.find(item => item.key === "total_number_of_invoice_types").gradient}
                            description={data.total_number_of_invoice_types?.description}
                            stat={formatIntegerNumberWithThousandSeparator(data.total_number_of_invoice_types?.data[0]?.Number)}
                        />
                        <StatisticsCardSingleStat
                            title={data.total_number_of_invoice_types?.data[1].name} bgColor={configuration.find(item => item.key === "total_number_of_invoice_types").gradient}
                            description={data.total_number_of_invoice_types?.description}
                            stat={formatIntegerNumberWithThousandSeparator(data.total_number_of_invoice_types?.data[1].Number)}
                        />
                        <StatisticsCardSingleStat
                            title={data.total_number_of_invoice_types?.data[2].name} bgColor={configuration.find(item => item.key === "total_number_of_invoice_types").gradient}
                            description={data.total_number_of_invoice_types?.description}
                            stat={formatIntegerNumberWithThousandSeparator(data.total_number_of_invoice_types?.data[2].Number)}
                        />
                        <StatisticsCardSingleStat
                            title={data.last_invoice_paid.name} bgColor={configuration.find(item => item.key === "last_invoice_paid").gradient}
                            description={"Payed by " + data.last_invoice_paid.data[0]?.name}
                            stat={friendlyDate(new Date(data.last_invoice_paid.data[0]?.payment_date.replace(" ", "T")), "-")}
                        />
                        <StatisticsCardSingleStat
                            title={data.count_invoice_without_type.name} bgColor={configuration.find(item => item.key === "count_invoice_without_type").gradient}
                            description={data.count_invoice_without_type.description}
                            onClick={() => showTableData("list_invoices_without_type")}
                            stat={formatIntegerNumberWithThousandSeparator(data.count_invoice_without_type.data[0]?.Number)}
                        />
                        <StatisticsCardSingleStat
                            title={data.unpaid_vendor_invoices.name} bgColor={configuration.find(item => item.key === "unpaid_vendor_invoices").gradient}
                            description={data.unpaid_vendor_invoices.description}
                            stat={formatIntegerNumberWithThousandSeparator(data.unpaid_vendor_invoices.data[0]?.Number)}
                        />
                        <StatisticsCardSingleStat
                            title={data.paid_vendor_invoices.name} bgColor={configuration.find(item => item.key === "paid_vendor_invoices").gradient}
                            description={data.paid_vendor_invoices.description}
                            stat={formatIntegerNumberWithThousandSeparator(data.paid_vendor_invoices.data[0]?.Number)}
                        />
                        <StatisticsCardSingleStat
                            title={data.paid_customer_invoices.name} bgColor={configuration.find(item => item.key === "paid_customer_invoices").gradient}
                            description={data.paid_customer_invoices.description}
                            stat={formatIntegerNumberWithThousandSeparator(data.paid_customer_invoices.data[0]?.Number)}
                        />
                        <StatisticsCardSingleStat
                            title={data.unpaid_customer_invoices.name} bgColor={configuration.find(item => item.key === "unpaid_customer_invoices").gradient}
                            description={data.unpaid_customer_invoices.description}
                            onClick={() => showTableData("list_unpaid_customer_invoices")}
                            stat={formatIntegerNumberWithThousandSeparator(data.unpaid_customer_invoices.data[0].Number)}
                        />
                        <StatisticsCardSingleStat
                            title={data.number_checked_in_cars_today.name} bgColor={configuration.find(item => item.key === "number_checked_in_cars_today").gradient}
                            description={data.number_checked_in_cars_today.description}
                            onClick={() => showTableData("list_checked_in_cars_today")}
                            stat={formatIntegerNumberWithThousandSeparator(data.number_checked_in_cars_today.data[0]?.Number || 0)}
                        />
                        <StatisticsCardSingleStat
                            title={data.number_checked_out_cars_today.name} bgColor={configuration.find(item => item.key === "number_checked_out_cars_today").gradient}
                            description={data.number_checked_out_cars_today.description}
                            onClick={() => showTableData("list_checked_out_cars_today")}
                            stat={formatIntegerNumberWithThousandSeparator(data.number_checked_out_cars_today.data[0]?.Number || 0)}
                        />
                        <StatisticsCardSingleStat
                            title={data.number_sold_unpaid_cars.name} bgColor={configuration.find(item => item.key === "number_sold_unpaid_cars").gradient}
                            description={data.number_sold_unpaid_cars.description}
                            onClick={() => showTableData("list_sold_unpaid_cars")}
                            stat={formatIntegerNumberWithThousandSeparator(data.number_sold_unpaid_cars.data[0]?.Number || 0)}
                        />
                    </div>
                </div>
                <DataListModal opened={opened} onClose={close}>
                    <DataTable data={tableData}/>
                </DataListModal>
            </>
        )
    }
}